import { useMemo } from "react";
import { FitComparison } from "./FitComparison";
import { RefreshCw } from "lucide-react";
import { CoachIcon } from "@/components/CoachIcon";
import type { GolfFitAssessment } from "@/services/schoolFits";
import { renderMarkdown } from "@/utils/markdown";
import { Button } from "@/components/ui/button";

interface SchoolFitAssessmentPanelProps {
  fit?: GolfFitAssessment | null;
  onRefresh: () => void;
  playerScoringDifferential?: number | null;
  playerClassRank?: number | null;
  programAverageClassRank?: number | null;
  programAverageScoringDifferential?: number | null;
  schoolName?: string;
  onAskCoach?: () => void;
  className?: string;
}

function explanationBody(value?: string | null): string {
  return String(value || "")
    .replace(/^\s*Golf Fit:\s*\d{1,3}\s*\/\s*100\s*[—–-]\s*(Dream|Realistic|Safety)\s*/i, "")
    .replace(/^\s*Golf Fit(?: Score)?:\s*Not enough data\s*/i, "")
    .trim();
}

function formatFitDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

export function SchoolFitAssessmentPanel({
  fit,
  onRefresh,
  playerScoringDifferential,
  programAverageScoringDifferential,
  playerClassRank,
  programAverageClassRank,
  schoolName,
  onAskCoach,
  className = "",
}: SchoolFitAssessmentPanelProps) {
  const evidence = fit?.evidence;
  const playerDifferential =
    evidence?.playerScoringDifferential ?? playerScoringDifferential ?? null;
  const programDifferential =
    evidence?.programAverageScoringDifferential ?? programAverageScoringDifferential ?? null;
  const fitHtml = useMemo(
    () => renderMarkdown(explanationBody(fit?.explanation)),
    [fit?.explanation]
  );
  const assessedDate = formatFitDate(fit?.assessedAt);
  const isRefreshing = fit?.status === "updating";
  const isUnavailable = fit?.status === "unavailable";

  return (
    <section className={`rounded-2xl border border-border bg-white p-4 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
            AI Coach Notes {assessedDate ? `· ${assessedDate}` : ""}
          </p>
          <h3 className="mt-1.5 font-display text-xl font-extrabold tracking-[-0.035em] text-forest">
            How your game fits
          </h3>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing || isUnavailable}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[11px] font-extrabold text-forest-soft hover:bg-surface disabled:opacity-55"
        >
          <RefreshCw className={`h-3 w-3 ${isRefreshing ? "animate-spin" : ""}`} />
          {isRefreshing
            ? "Assessing"
            : isUnavailable
              ? "Needs JGS data"
              : fit?.score != null
                ? "Refresh"
                : "Assess fit"}
        </button>
      </div>

      {fitHtml ? (
        <div
          className="prose prose-sm mt-3 max-w-none text-[11px] leading-[1.65] text-forest-soft prose-headings:font-display prose-headings:text-forest prose-p:my-2 prose-li:my-1"
          dangerouslySetInnerHTML={{ __html: fitHtml }}
        />
      ) : (
        <p className="mt-3 text-[11px] leading-[1.65] text-muted-foreground">
          {isUnavailable
            ? "The AI Coach needs a JGS class rank, scoring differential, and class year before it can score this program."
            : fit?.status === "failed"
              ? "The AI Coach could not refresh this assessment. Try again when you’re ready."
              : "Ask the AI Coach to compare your verified golf data with this program’s recent recruits."}
        </p>
      )}

      <FitComparison
        playerRank={evidence?.playerClassRank ?? playerClassRank ?? null}
        programRank={evidence?.programAverageClassRank ?? programAverageClassRank ?? null}
        playerDifferential={playerDifferential}
        programDifferential={programDifferential}
        variant="tiles"
        className="mt-3"
      />

      {schoolName && onAskCoach && (
        <div className="mt-4 rounded-xl border border-primary/15 bg-primary/5 p-3.5">
          <p className="text-xs font-semibold leading-relaxed text-foreground">
            Want to learn more about {schoolName} and its golf program?
          </p>
          <Button type="button" size="sm" onClick={onAskCoach} className="mt-3 w-full text-xs">
            <CoachIcon className="h-4 w-4" /> Ask AI Coach
          </Button>
        </div>
      )}
    </section>
  );
}
